import type { QualityAcceptance, QualityAcceptanceProperty } from '@/types/buy';

/** Кастомная надбавка/скидка приёмки по свойству (byGost=false): порог % и снижение веса %. */
export interface QaOverride {
  readonly inc: number | string;
  readonly dec: number | string;
}
export type QaOverrides = Record<string, QaOverride>;

/** Пустое (не заданное) поле или нечисловой ввод → откат к дефолту (а не Number('')===0). */
function toNumber(value: number | string | undefined, fallback: number): number {
  if (value === '' || value == null) {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Префилл порогов из дефолтов свойств (как при byGost=false в форме прайса). */
export function initQaOverrides(qaProps: readonly QualityAcceptanceProperty[]): QaOverrides {
  const out: Record<string, QaOverride> = {};
  for (const property of qaProps) {
    out[property.name] = { inc: property.increaseValue ?? '', dec: property.decreaseWeightValue ?? '' };
  }
  return out;
}

/**
 * Сборка qualityAcceptance для payload сделки/прайса (единый источник, как старый buy-offer-acceptance):
 * ГОСТ → только name+gostValue; иначе по каждому свойству — порог (increaseValue) + снижение веса
 * (decreaseWeightValue) из переопределений или дефолтов свойства.
 */
export function buildQualityAcceptance(
  byGost: boolean,
  overrides: QaOverrides,
  qaProps: readonly QualityAcceptanceProperty[],
): QualityAcceptance {
  return {
    byGost,
    qaProperties: qaProps.map((property) =>
      byGost
        ? { name: property.name, gostValue: property.gostValue }
        : {
            name: property.name,
            increaseValue: toNumber(overrides[property.name]?.inc, property.increaseValue ?? 0),
            decreaseWeightValue: toNumber(overrides[property.name]?.dec, property.decreaseWeightValue ?? 0),
          },
    ),
  };
}

/** Готовность приёмки: ГОСТ, либо нет свойств, либо у каждого свойства заданы оба порога. */
export function isAcceptanceComplete(
  byGost: boolean,
  overrides: QaOverrides,
  qaProps: readonly QualityAcceptanceProperty[],
): boolean {
  if (byGost || qaProps.length === 0) {
    return true;
  }
  return qaProps.every((property) => {
    const override = overrides[property.name];
    return override != null && override.inc !== '' && override.dec !== '';
  });
}
